import { createApi } from "@reduxjs/toolkit/query/react";

import { REQUEST_METHODS } from "@/constants/global-constants";
import {
  IEmployee,
  IEmployeeDetails,
  IEmployeeOptionsList,
} from "@/interfaces/employee.interface";
import { deviceReqType } from "@/interfaces/interfaces";
import { TGetSingleEmployee } from "@/schemas/employee-schema";
import { BaseWithAuth, getToken, getWorkspace } from "@/utils/helpers";

export const employeeApi = createApi({
  reducerPath: "employeeApi",
  baseQuery: BaseWithAuth(),
  tagTypes: ["employee", "employeeOptions"],
  endpoints: (builder) => ({
    // get list of employee
    getEmployeeList: builder.query<
      GeneralResponse<IEmployee[]>,
      Partial<{
        status: string;
        filter?: string;
        approvedStatus?: "PENDING" | "APPROVED";
      }>
    >({
      query: ({ status, filter, approvedStatus }) =>
        `/superadmin/workforce/employee${status ? `?status=${status}` : ""}${filter ? filter : ""}${approvedStatus ? `&approvedStatus=${approvedStatus}` : ""}`,
      providesTags: ["employee"],
    }),

    // get list of employee according to the  workspace
    getEmployeeByWS: builder.query<
      GeneralResponse<IEmployee[]>,
      Partial<{
        status: string;
        filter?: string;
        approvedStatus?: "PENDING" | "APPROVED";
      }>
    >({
      query: ({ status, filter, approvedStatus }) =>
        `workspace/${getWorkspace()}/workforce/employee${status ? `?status=${status}` : ""}${filter ? filter : ""}${approvedStatus ? `&approvedStatus=${approvedStatus}` : ""}`,
      providesTags: ["employee"],
    }),
    // toggle employee status
    toggleEmployeeStatusWs: builder.mutation<
      unknown,
      { userId: string; status: string; data?: object }
    >({
      query: ({ userId, status, data }) => ({
        url: `/workspace/${getWorkspace()}/workforce/employee/${userId}/${status}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["employee"],
    }),
    //alter employee
    alterEmployeeByWs: builder.mutation<
      object,
      Partial<{
        data: object;
        userId: string;
        method?: string;
      }>
    >({
      query: ({ data, userId, method }) => ({
        url: `workspace/${getWorkspace()}/workforce/employee/${userId ? userId : "ss"}`,
        method: method?.toUpperCase() || "PUT",
        body: data,
      }),
      invalidatesTags: ["employee"],
    }),

    getSingleEmployee: builder.query<
      TGetSingleEmployee,
      { employeeId: string }
    >({
      query: ({ employeeId }) =>
        `/workspace/${getWorkspace()}/workforce/employee/${employeeId}`,
      providesTags: ["employee"],
      transformResponse: (data: APIResponse<TGetSingleEmployee>) => data.data,
    }),
    getEmployeeDevices: builder.query<
      GeneralResponse<IEmployee[]>,
      { user?: string; filter?: string }
    >({
      query: ({ user, filter }) => {
        return user === "admin"
          ? `/superadmin/workforce/employee/devices${filter || ""}`
          : `/workspace/${getWorkspace()}/workforce/employee/devices${filter || ""}`;
      },
      providesTags: ["employee"],
    }),

    getEmployeeOptions: builder.query<
      IEmployeeOptionsList[],
      { filters: string }
    >({
      query: ({ filters }) => ({
        url: `workspace/${getWorkspace()}/workforce/employee/options${filters}`,
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }),
      providesTags: ["employeeOptions"],
      transformResponse: (response: APIResponse<IEmployeeOptionsList[]>) =>
        response?.data,
    }),
    addEmployee: builder.mutation<object, { data: object }>({
      query: ({ data }) => ({
        url: `workspace/${getWorkspace()}/workforce/employee`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["employee"],
    }),

    updateEmployee: builder.mutation<
      unknown,
      { employeeId: string; data: object }
    >({
      query: ({ employeeId, data }) => ({
        url: `/workspace/${getWorkspace()}/workforce/employee/${employeeId}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["employee"],
    }),
    updateEmployeeWork: builder.mutation<
      unknown,
      { employeeId: string; data: object }
    >({
      query: ({ employeeId, data }) => ({
        url: `/workspace/${getWorkspace()}/workforce/employee/${employeeId}/basic-info`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["employee"],
    }),
    updateEmployeeImportant: builder.mutation<
      unknown,
      { employeeId: string; data: object }
    >({
      query: ({ employeeId, data }) => ({
        url: `/workspace/${getWorkspace()}/workforce/employee/${employeeId}/important-info`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["employee"],
    }),
    updateWorkInfo: builder.mutation<
      object,
      { employeeId: string; data: object }
    >({
      query: ({ employeeId, data }) => ({
        url: `/workspace/${getWorkspace()}/workforce/employee/${employeeId}/work-info`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["employee"],
    }),
    updateEmployeeFinalEmployee: builder.mutation<
      unknown,
      { employeeId: string; data: object }
    >({
      query: ({ employeeId, data }) => ({
        url: `/workspace/${getWorkspace()}/workforce/employee/${employeeId}/declaration-info`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["employee"],
    }),

    updateEmployeeOptions: builder.mutation<
      object,
      {
        id?: string;
        data?: object;
        method: Omit<REQUEST_METHODS, "PUT">;
      }
    >({
      query: ({ id, data, method }) => ({
        url: `workspace/${getWorkspace()}/workforce/employee/options/${id ? id : ""}`,
        method: method.toUpperCase(),
        body: data,
      }),
      invalidatesTags: ["employeeOptions"],
    }),

    // handle device request
    removeDevice: builder.mutation<
      deviceReqType,
      { userId: string; method: string }
    >({
      query: ({ userId, method }) => ({
        url: `workspace/${getWorkspace()}/workforce/employee/${userId}/${method}`,
        method: "POST",
      }),
      invalidatesTags: ["employee"],
    }),
    employeeAccessOtp: builder.query<object, { id: string }>({
      query: ({ id }) => ({
        url: `workspace/${getWorkspace()}/workforce/employee/${id}/access`,
      }),
    }),
    verifyEmployeeAccessOtp: builder.query<
      APIResponse<IEmployeeDetails>,
      { id: string; otp: string }
    >({
      query: ({ id, otp }) => ({
        url: `workspace/${getWorkspace()}/workforce/employee/${id}/important?otp=${otp}`,
      }),
    }),
    approveEmployee: builder.mutation<
      APIResponse<IEmployeeDetails>,
      { id: string }
    >({
      query: ({ id }) => ({
        url: `workspace/${getWorkspace()}/workforce/employee/${id}/approve`,
        method: "POST",
      }),
      invalidatesTags: ["employee"],
    }),
  }),
});

export const {
  useGetEmployeeListQuery,
  useGetEmployeeByWSQuery,
  useGetSingleEmployeeQuery,
  useLazyGetSingleEmployeeQuery,
  useGetEmployeeDevicesQuery,
  useUpdateEmployeeImportantMutation,
  useAddEmployeeMutation,
  useUpdateEmployeeMutation,
  useUpdateEmployeeWorkMutation,
  useUpdateWorkInfoMutation,
  useUpdateEmployeeFinalEmployeeMutation,
  useRemoveDeviceMutation,
  useGetEmployeeOptionsQuery,
  useUpdateEmployeeOptionsMutation,
  useEmployeeAccessOtpQuery,
  useVerifyEmployeeAccessOtpQuery,
  useLazyVerifyEmployeeAccessOtpQuery,
  useApproveEmployeeMutation,
  useToggleEmployeeStatusWsMutation,
  useAlterEmployeeByWsMutation,
} = employeeApi;
